/**
 *
 * NewTreatmentPage
 *
 */
import {Component, createRef} from "react";
import * as PropTypes from "prop-types";
import styled from "styled-components";
import {connect} from "react-redux";
import {createStructuredSelector} from "reselect";
import {compose} from "redux";
import AppContainer from "components/AppContainer/AppContainer";
import injectSaga from "utils/injectSaga";
import injectReducer from "utils/injectReducer";
import MultiStep from "components/MultiStep/MultiStep";
import NewTreatmentUserStep from "containers/newTreatment/NewTreatmentUserStep/NewTreatmentUserStep";
import NewTreatmentTreatmentStep from "containers/newTreatment/NewTreatmentTreatmentStep/NewTreatmentTreatmentStep";
import NewTreatmentPlanningStep from "containers/newTreatment/NewTreatmentPlanningStep/NewTreatmentPlanningStep";
import NewTreatmentResultStep from "containers/newTreatment/NewTreatmentResultStep/NewTreatmentResultStep";
import history from "utils/history";
import {
  makeSelectDentistApp,
  makeSelectEndProductTypes,
  makeSelectEndProductTypesLoading,
  makeSelectFilteredPatients,
  makeSelectInsurersApp,
} from "containers/App/selectors";
import {getDentistActionApp, getEndProductTypesAction, getInsurersActionApp,} from "containers/App/actions";
import ForegroundBlob from "basicComponents/ForegroundBlob/ForegroundBlob";
import {FormattedMessage} from "react-intl";
import makeSelectNewTreatmentPage, {
  makeSelectDontSkip,
  makeSelectHasPatient,
  makeSelectIsEditingPlannedTreatment,
  makeSelectPatient,
  makeSelectPatientIdLoading,
  makeSelectTreatmentToPlan,
  makeSelectUserStepLoading,
} from "./selectors";
import reducer from "./reducer";
import saga from "./saga";
import messages from "./messages";
import {
  continueAction,
  getPatientByIdAction,
  getTreatmentPlanByIdAction,
  hardResetAction,
  isEditingPlannedTreatment,
  resetAction,
  selectTimeSlot,
  setEditingTreatmentAction,
  setPatientAction,
  setTreatmentPlanAction,
  setTreatmentToPlanAction,
} from "./actions";
import {makeSelectPractice} from "../PracticePage/selectors";
import NewTreatmentLoading from "./NewTreatmentLoading";
import LoadingAnimation from "../../basicComponents/LoadingAnimation/LoadingAnimation";
import {makeSelectTreatmentFromTimeSlot} from "../PageState/selectors";

export class NewTreatmentPage extends Component {
  static propTypes = {
    filteredPatients: PropTypes.array,
    getInsurers: PropTypes.func.isRequired,
    practice: PropTypes.object.isRequired,
    hasPatient: PropTypes.bool,
    continue: PropTypes.func.isRequired,
    hardReset: PropTypes.func.isRequired,
    dontSkip: PropTypes.bool,
    match: PropTypes.object.isRequired,
    setPatient: PropTypes.func.isRequired,
    getPatientById: PropTypes.func.isRequired,
    getTreatmentPlanById: PropTypes.func.isRequired,
    endProductTypes: PropTypes.array,
    getEndProductTypes: PropTypes.func.isRequired,
    userStepLoading: PropTypes.bool,
    setTreatmentToPlan: PropTypes.func.isRequired,
    patient: PropTypes.object,
    insurers: PropTypes.array.isRequired,
    isEditingPlannedTreatment: PropTypes.bool,
    setEditingTreatment: PropTypes.func.isRequired,
    endProductTypesLoading: PropTypes.bool,
    dentists: PropTypes.array.isRequired,
    patientIdLoading: PropTypes.bool,
    treatmentFromTimeSlot: PropTypes.object,
    setTreatmentPlan: PropTypes.func.isRequired,
    treatmentToPlan: PropTypes.object,
    setTimeSlot: PropTypes.func.isRequired,
    setEditing: PropTypes.func.isRequired,
  };

  static defaultProps = {
    filteredPatients: [],
    endProductTypes: [],
  };

  constructor(props) {
    super(props);
    this.multiStepRef = createRef();

    window.currentnewtreatmentpage = this
  }

  state = {
    showCancelTreatmentModal: false,
    date: null,
  };

  /**
   * Check if there is a patient parameter provided and get a patient based on it
   * @returns {boolean} true if there is a patient, false otherwise.
   */
  checkPatientParam(patientId) {
    if (Number.isNaN(patientId) || patientId === 0) {
      return false;
    }
    this.props.getPatientById(patientId);
    return true;
  }

  /**
   * Check if there is a treatmentPlan parameter provided and get a treatmentPlan
   * based on it.
   * @returns {boolean} true if there is a treatmentPlan, false otherwise.
   */
  checkTreatmentPlanParam(treatmentPlanId) {
    if (Number.isNaN(treatmentPlanId) || treatmentPlanId === 0) {
      return false;
    }
    this.props.getTreatmentPlanById(treatmentPlanId);
    return true;
  }

  /**
   * Check any params and get their corresponding values
   */
  checkParams() {
    // If we have a patientId in the params, find and set the patient
    const { params } = this.props.match;
    if (params.patientId) {
      this.checkPatientParam(Number.parseInt(params.patientId, 10));
    }

    // If we have a treatmentPlan, find an set the treatmentPlan and patient
    if (params.treatmentPlanId) {
      this.checkTreatmentPlanParam(Number.parseInt(params.treatmentPlanId, 10));
    }

    // If there is a plannedTreatment in the location variables, we're editing a planned treatment
    if (history.location.plannedTreatment) {
      this.props.setTreatmentToPlan(history.location.plannedTreatment);
    }
  }

  cameFromTimeSlot() {
    return !!this.props.treatmentFromTimeSlot;
  }

  stateFromTimeSlotPage() {
    const { treatmentFromTimeSlot } = this.props;
    this.props.setEditingTreatment(true);
    this.props.setTreatmentToPlan(treatmentFromTimeSlot.treatmentToPlan);
    this.props.setPatient(treatmentFromTimeSlot.patient);
    this.props.setTreatmentPlan(treatmentFromTimeSlot.treatmentPlan);
    if (treatmentFromTimeSlot.timeSlot) {
      this.props.setTimeSlot(treatmentFromTimeSlot.timeSlot);
    }
  }

  setEditing() {
    if (history.location.editing) {
      this.props.setEditing(true);
    }
  }

  componentDidMount() {
    window.onbeforeunload = () => "Changes will not be saved";

    this.setEditing();
    this.props.getDentistsApp();
    // We were previously planning a treatment and paged to the timeSlotPage and back.
    if (this.cameFromTimeSlot()) {
      this.stateFromTimeSlotPage();
    } else {
      // Check if we have a plannedTreatment in history and set it as a boolean
      this.props.setEditingTreatment(!!history.location.plannedTreatment);
      // Check if there are any parameters and get their values.
      this.checkParams();
    }

    if (this.props.insurers.length === 0) {
      this.props.getInsurers();
    }

    if (this.props.endProductTypes.length === 0) {
      this.props.getEndProductTypes();
    }
  }

  componentWillUnmount() {
    window.onbeforeunload = null;
    // Make sure to reset after closing.
    this.props.hardReset();
  }

  /**
   * make sure the multistep gets reset to the start.
   */
  resetMultiStep() {
    if (this.multiStepRef && this.multiStepRef.current) {
      this.multiStepRef.current.reset();
    }
  }

  /**
   * Close the page by going back to the previous one.
   */
  handleClose = () => {
    if (history.location.fromAdmin === true) {
      history.push(`/admin/agendas`);
    } else if (history.location.state && history.location.state.fromTimeSlot) {
      history.push(`/timeslots/${history.location.state.fromTimeSlot}`);
    } else if (history.location.state && history.location.state.fromDashboard) {
      history.push("/dashboard");
    } else if (
      history.location.state &&
      history.location.state.fromTreatmentFlow
    ) {
      if (history.location.state && history.location.state.searchedDate) {
        history.push(`/timeslots/${history.location.state.searchedDate}`, {
          searchedDate: history.location.state.searchedDate,
        });
      } else {
        history.push("/dashboard");
      }
    } else if (history.length > 2) {
      // history.goBack();
      history.push(`/patient/${this.props.patient.id}`)
    } else {
      history.push("/dashboard");
    }
  };

  /**
   * When a treatment has been planned, and the user continues to plan another treatment.
   */
  handleContinue = () => {
    this.resetMultiStep();
    this.props.continue();
  };

  /**
   * Check if it should only edit an already planned treatment.
   */
  get shouldEditPlannedTreatment() {
    return (
      this.props.isEditingPlannedTreatment || history.location.plannedTreatment
    );
  }

  /**
   * handle opening the modal to exit this page
   */
  handleModalOpen = () => {
    if (this.state.showCancelTreatmentModal) {
      this.setState({ showCancelTreatmentModal: false });
    }

    this.setState({ showCancelTreatmentModal: true });
  };

  /**
   * Handle closing the modal to exit this page.
   */
  handleCloseModal = () => {
    this.setState({ showCancelTreatmentModal: false });
  };

  render() {
    if (this.cameFromTimeSlot() && !this.props.treatmentToPlan) {
      return null;
    }
    // Show the user step if we don't have a patient.
    const shouldShowUserStep = !this.props.hasPatient || this.props.dontSkip;
    // The steps to display
    let steps = [NewTreatmentPlanningStep, NewTreatmentResultStep];
    let loading = false;
    // The step to start on
    let initialStep = 0;
    let shouldReset = true;
    // The props passed to every step
    const props = {
      shouldShowUserStep,
      practice: this.props.practice,
      onContinue: this.handleContinue,
      onClose: this.handleClose,
      patient: this.props.patient,
      hasPatient: this.props.hasPatient,
      insurers: this.props.insurers.slice(),
      dentists: this.props.dentists.slice(),
      showAnimation: true,
      handleCloseModal: this.handleCloseModal,
      handleModalOpen: this.handleModalOpen,
      reFittingPlannedTreatment: history?.location?.reFittingPlannedTreatment || null
    };

    // Are we editing a planned treatment, or planning a new one
    // if (this.shouldEditPlannedTreatment) {
    //   if (this.props.userStepLoading || !this.props.hasPatient) {
    //     loading = true;
    //     steps.unshift(NewTreatmentLoading);
    //     initialStep = 1;
    //   } else {
    //     steps.unshift(NewTreatmentTreatmentStep);
    //     initialStep = 1;
    //   }

    //   props.treatmentToPlan = history.location.plannedTreatment;
    // } else {
    //   shouldReset = false;
    //   // When not editing a treatment add additional steps.
    //   steps.unshift(NewTreatmentTreatmentStep);
    //   if (shouldShowUserStep) {
    //     shouldReset = true;
    //     steps.unshift(NewTreatmentUserStep);
    //   }
    // }

    const StepsLoading =
      this.props.endProductTypesLoading || this.props.patientIdLoading;

    if (this.cameFromTimeSlot()) {
      initialStep = 1;
    }

    return (
      <div>
        <AppContainer
          patient={this.props.patient}
          color="green"
          header={<FormattedMessage {...messages.appointment} />}
        >
          <ForegroundBlob noLargePadding>
            {StepsLoading ? (
              <CenterDiv>
                <LoadingAnimation />
              </CenterDiv>
            ) : (
              <MultiStep
                initialStep={initialStep}
                onReset={this.props.hardReset}
                modal={this.state.showCancelTreatmentModal}
                showProgress={!loading}
                ref={this.multiStepRef}
                shouldReset={shouldReset}
                stepProps={props}
                steps={steps.slice()}
              />
            )}
          </ForegroundBlob>
        </AppContainer>
      </div>
    );
  }
}

const CenterDiv = styled.div`
  width: 100%;
  height: 100%;
  min-height: 865px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const mapStateToProps = createStructuredSelector({
  NewTreatmentPage: makeSelectNewTreatmentPage(),
  practice: makeSelectPractice(),
  hasPatient: makeSelectHasPatient(),
  dontSkip: makeSelectDontSkip(),
  filteredPatients: makeSelectFilteredPatients(),
  endProductTypes: makeSelectEndProductTypes(),
  userStepLoading: makeSelectUserStepLoading(),
  patient: makeSelectPatient(),
  insurers: makeSelectInsurersApp(),
  dentists: makeSelectDentistApp(),
  patientIdLoading: makeSelectPatientIdLoading(),
  isEditingPlannedTreatment: makeSelectIsEditingPlannedTreatment(),
  endProductTypesLoading: makeSelectEndProductTypesLoading(),
  treatmentFromTimeSlot: makeSelectTreatmentFromTimeSlot(),
  treatmentToPlan: makeSelectTreatmentToPlan(),
});

function mapDispatchToProps(dispatch) {
  return {
    getEndProductTypes: () => dispatch(getEndProductTypesAction()),
    getInsurers: () => dispatch(getInsurersActionApp()),
    hardReset: () => dispatch(hardResetAction()),
    softReset: () => dispatch(resetAction()),
    continue: () => dispatch(continueAction()),
    setPatient: patient => dispatch(setPatientAction(patient, false)),
    getPatientById: patientId => dispatch(getPatientByIdAction(patientId)),
    getTreatmentPlanById: treatmentPlanId =>
      dispatch(getTreatmentPlanByIdAction(treatmentPlanId)),
    setTreatmentToPlan: treatment =>
      dispatch(setTreatmentToPlanAction(treatment)),
    setEditingTreatment: isEditing =>
      dispatch(isEditingPlannedTreatment(isEditing)),
    setTreatmentPlan: treatmentPlan =>
      dispatch(setTreatmentPlanAction(treatmentPlan)),
    setTimeSlot: timeSlot => dispatch(selectTimeSlot(timeSlot)),
    setEditing: editing => dispatch(setEditingTreatmentAction(editing)),
    getDentistsApp: () => dispatch(getDentistActionApp()),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

const withReducer = injectReducer({ key: "newTreatmentPage", reducer });
const withSaga = injectSaga({ key: "newTreatmentPage", saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(NewTreatmentPage);
